import React, { useState, useEffect } from 'react';
import { IoClose, IoSaveOutline, IoImageOutline, IoArrowBackOutline, IoArrowForwardOutline, IoHappyOutline } from 'react-icons/io5';
import imageCompression from 'browser-image-compression';
import EmojiPicker from './EmojiPicker';

interface DiaryEntry {
  id: number;
  userId: number;
  title: string;
  body: string;
  date: string;
  imageDataUrls: string[];
  createdAtIst: string;
  updatedAtIst: string;
}

interface DiaryEditorProps {
  userId: number;
  entry?: DiaryEntry | null;
  onClose: () => void;
  onSave: () => void;
}

export const DiaryEditor: React.FC<DiaryEditorProps> = ({ userId, entry, onClose, onSave }) => {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [date, setDate] = useState('');
  const [images, setImages] = useState<string[]>([]);
  const [newImages, setNewImages] = useState<File[]>([]);
  const [saving, setSaving] = useState(false);
  const [compressing, setCompressing] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [currentEntry, setCurrentEntry] = useState<DiaryEntry | null>(entry || null);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrevious, setHasPrevious] = useState(false);

  useEffect(() => {
    setCurrentEntry(entry || null);
  }, [entry]);

  useEffect(() => {
    if (currentEntry) {
      setTitle(currentEntry.title);
      setBody(currentEntry.body);
      setDate(currentEntry.date);
      setImages(currentEntry.imageDataUrls || []);
      setNewImages([]);
      checkNavigation(currentEntry.id);
    } else {
      setTitle('');
      setBody('');
      setDate(new Date().toISOString().split('T')[0]);
      setImages([]);
      setNewImages([]);
    }
  }, [currentEntry]);

  const checkNavigation = async (entryId: number) => {
    try {
      const nextRes = await fetch(`/api/diary/${entryId}/next`);
      setHasNext(nextRes.ok);

      const prevRes = await fetch(`/api/diary/${entryId}/previous`);
      setHasPrevious(prevRes.ok);
    } catch (error) {
      console.error('Error checking navigation:', error);
    }
  };

  const handleNavigate = async (direction: 'next' | 'previous') => {
    if (!currentEntry) return;

    try {
      const response = await fetch(`/api/diary/${currentEntry.id}/${direction}`);
      if (response.ok) {
        const data = await response.json();
        setCurrentEntry(data);
      }
    } catch (error) {
      console.error(`Error navigating to ${direction}:`, error);
    }
  };

  const handleImageSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setCompressing(true);

    try {
      const compressedFiles: File[] = [];
      
      for (const file of Array.from(files)) {
        try {
          const options = {
            maxSizeMB: 1,
            maxWidthOrHeight: 1920,
            useWebWorker: true
          };
          
          const compressedFile = await imageCompression(file, options);
          compressedFiles.push(compressedFile);
          
          console.log(`Compressed ${file.name}: ${file.size} → ${compressedFile.size} (${Math.round((1 - compressedFile.size / file.size) * 100)}% reduction)`);
        } catch (error) {
          console.error('Compression error:', error);
          compressedFiles.push(file);
        }
      }

      setNewImages([...newImages, ...compressedFiles]);
    } finally {
      setCompressing(false);
      e.target.value = '';
    }
  };

  const removeNewImage = (index: number) => {
    setNewImages(newImages.filter((_, i) => i !== index));
  };

  const handleEmojiSelect = (emoji: string) => {
    setBody(body + emoji);
    setShowEmojiPicker(false);
  };

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = (reader.result as string).split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleSave = async () => {
    if (!title.trim()) {
      alert('Please enter a title');
      return;
    }

    if (!date) {
      alert('Please select a date');
      return;
    }

    setSaving(true);

    try {
      const imageDataList = await Promise.all(
        newImages.map(async (file) => ({
          base64Data: await fileToBase64(file),
          mimeType: file.type
        }))
      );

      const requestBody = {
        userId: userId,
        title: title,
        body: body,
        date: date,
        images: imageDataList.length > 0 ? imageDataList : undefined
      };

      const url = currentEntry 
        ? `/api/diary/${currentEntry.id}` 
        : '/api/diary';
      
      const method = currentEntry ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        throw new Error('Failed to save diary entry');
      }

      setNewImages([]);
      onSave();
    } catch (error) {
      console.error('Error saving diary:', error);
      alert('Failed to save diary entry');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4">
      <div className="bg-gray-900 rounded-lg w-full max-w-4xl max-h-[90vh] flex flex-col">
        <div className="p-4 border-b border-gray-700 flex items-center justify-between">
          <div className="flex items-center gap-2">
            {currentEntry && (
              <>
                <button
                  onClick={() => handleNavigate('previous')}
                  disabled={!hasPrevious}
                  className="p-2 text-gray-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  title="Previous entry"
                >
                  <IoArrowBackOutline size={24} />
                </button>
                <button
                  onClick={() => handleNavigate('next')}
                  disabled={!hasNext}
                  className="p-2 text-gray-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  title="Next entry"
                >
                  <IoArrowForwardOutline size={24} />
                </button>
                <div className="h-6 w-px bg-gray-700 mx-2"></div>
              </>
            )}
            <h2 className="text-xl font-semibold text-white">
              {currentEntry ? 'Edit Entry' : 'New Entry'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <IoClose size={24} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Date
            </label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-full bg-gray-800 text-white rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Title
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter title..."
              className="w-full bg-gray-800 text-white rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Story
            </label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Write your story..."
              rows={12}
              className="w-full bg-gray-800 text-white rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            />
            <div className="mt-2 flex items-center gap-2">
              <button
                onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                className="p-2 text-gray-400 hover:text-white transition-colors"
                title="Add emoji"
              >
                <IoHappyOutline size={24} />
              </button>
              {showEmojiPicker && (
                <div className="absolute z-10">
                  <EmojiPicker 
                    onEmojiSelect={handleEmojiSelect}
                    onClose={() => setShowEmojiPicker(false)}
                  />
                </div>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Images
            </label>
            
            {images.length > 0 && (
              <div className="mb-3 grid grid-cols-3 gap-2">
                {images.map((imagePath, index) => (
                  <div key={index} className="relative aspect-square">
                    <img
                      src={imagePath}
                      alt={`Diary ${index + 1}`}
                      className="w-full h-full object-cover rounded-lg"
                    />
                  </div>
                ))}
              </div>
            )}

            {newImages.length > 0 && (
              <div className="mb-3 grid grid-cols-3 gap-2">
                {newImages.map((file, index) => (
                  <div key={index} className="relative aspect-square">
                    <img
                      src={URL.createObjectURL(file)}
                      alt={`New ${index + 1}`}
                      className="w-full h-full object-cover rounded-lg"
                    />
                    <button
                      onClick={() => removeNewImage(index)}
                      className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                    >
                      <IoClose size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <label className="flex items-center justify-center gap-2 px-4 py-3 bg-gray-800 text-gray-300 rounded-lg cursor-pointer hover:bg-gray-700 transition-colors">
              <IoImageOutline size={24} />
              {compressing ? 'Compressing...' : 'Add Images'}
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleImageSelect}
                className="hidden"
                disabled={compressing}
              />
            </label>
          </div>
        </div>

        <div className="p-4 border-t border-gray-700 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 py-3 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || compressing}
            className="flex-1 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            <IoSaveOutline size={24} />
            {saving ? 'Saving...' : 'Save Entry'}
          </button>
        </div>
      </div>
    </div>
  );
};
